import { Autocomplete, TextField } from '@mui/material'
import { Axios } from 'api/axios'
import { useFormik } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import { Button, Card, Col, Form, Row } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import * as Yup from 'yup'

const TeamstForm = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch<any>()
  const location = useLocation();
  const teamsData = location.state?.teams;

  const [organisations, setOrganisations] = useState<{ id: string; name: string }[]>([])
  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([])

  const formik = useFormik({
    initialValues: {
      organisationId: '',
      departmentId: '',
      team_name: '',
    },
    validationSchema: Yup.object({
      organisationId: Yup.string().required('Organisation is required'),
      departmentId: Yup.string().required('Department is required'),
      team_name: Yup.string().required('Team Name is required'),
    }),
    onSubmit: async (values) => {
      try {
        const payload = {
          organization: values.organisationId,
          department: values.departmentId,
          team_name: values.team_name,
        };

        let response;
        if (id) {
          // Editing existing team
          response = await Axios.put(`/api/team/edit/${id}/`, payload);
        } else {
          // Creating new team
          response = await Axios.post('/api/team/create/', payload);
        }

        if (response.status === 200 || response.status === 201) {
          toast.success(id ? "Team updated successfully" : "Team created successfully");
          window.history.length > 1 ? navigate(-1) : navigate("/team-list");
        }
      } catch (error) {
        toast.error('The team name already exists');
      }
    }
  })

  useEffect(() => {
    Axios.get('/api/user/organization-dropdown/')
      .then(res => {
        const mapped = res.data?.data?.map((org: any) => ({
          id: org.organization_id,
          name: org.organization_name,
        })) || []
        setOrganisations(mapped)
      })
      .catch(error => console.error('Failed to fetch organisations:', error))
  }, [])

  useEffect(() => {
    if (!formik.values.organisationId) {
      setDepartments([])
      formik.setFieldValue('departmentId', '', false)
      return
    }

    Axios.get(`/api/user/department-dropdown/${formik.values.organisationId}/`)
      .then(res => {
        const mapped = res.data?.data?.map((dept: any) => ({
          id: dept.department_id,
          name: dept.department_name
        })) || []

        setDepartments(mapped)

        // Only reset department if current selection is not valid
        if (!mapped.find((d: any) => d.id === formik.values.departmentId)) {
          formik.setFieldValue('departmentId', '', false)
        }
      })
      .catch(error => {
        console.error('Failed to fetch departments:', error)
        setDepartments([])
        formik.setFieldValue('departmentId', '', false)
      })
  }, [formik.values.organisationId])

  useEffect(() => {
    console.log(teamsData, "teamsData----2")
    if (id && teamsData) {
      formik.setValues({
        organisationId: teamsData.organization || '',
        departmentId: teamsData.department || '',
        team_name: teamsData.team_name || '',
      });
    }
  }, [id, teamsData]);

  const safeOrgOptions = useMemo(() => {
    if (formik.values.organisationId && !organisations.some(org => org.id === formik.values.organisationId)) {
      return [{ id: formik.values.organisationId, name: '' }, ...organisations]
    }
    return organisations
  }, [formik.values.organisationId, organisations])

  const safeDeptOptions = useMemo(() => {
    if (formik.values.departmentId && !departments.some(dept => dept.id === formik.values.departmentId)) {
      return [{ id: formik.values.departmentId, name: '' }, ...departments]
    }
    return departments
  }, [formik.values.departmentId, departments])

  return (
    <div className='add-organisation-container p-4 rounded bg-white' style={{ marginTop: '6rem', marginLeft: '2rem', marginRight: '2rem' }}>
      <div className='mb-4'>
        <div className='d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2'>
          <div className='d-flex align-items-center'>
            <span style={{ cursor: 'pointer', fontSize: '1.5rem', lineHeight: 1 }} onClick={() => navigate(-1)}>
              <i className='bi bi-arrow-left'></i>
            </span>
            <h5 className='mb-0 fw-bold ms-2'>{id ? 'Edit' : 'Create'} Team</h5>
          </div>
          <div className='text-muted' style={{ fontSize: '0.9rem' }}>
            <Link to='/team-list' className='text-decoration-none text-muted'>Team</Link> &gt;{' '}
            <Link to='' className='text-decoration-none text-muted'>{id ? 'Edit Team' : 'Add Team'}</Link>
          </div>
        </div>
      </div>

      <Card className='p-4 shadow-sm mt-4'>
        <Form noValidate onSubmit={formik.handleSubmit}>
          <Row className='mb-4'>

            {/* Organisation */}
            <Col md={4} xs={12} className='mb-3 mb-md-0'>
              <Form.Label className='fw-semibold'>
                Organisation <span className='text-danger'>*</span>
              </Form.Label>
              <Autocomplete
                disablePortal
                options={safeOrgOptions}
                getOptionLabel={(option: any) => option.name || ''}
                value={safeOrgOptions.find(org => org.id === formik.values.organisationId) || null}
                onChange={(_, value) => {
                  formik.setFieldValue('organisationId', value?.id || '')
                }}
                onBlur={() => {
                  formik.setFieldTouched('organisationId', true)
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder='Select Organisation'
                    error={formik.touched.organisationId && Boolean(formik.errors.organisationId)}
                    helperText={formik.touched.organisationId && formik.errors.organisationId}
                    fullWidth
                    size='small'
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#dc3545',
                        },
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                )}
              />
            </Col>

            {/* Department */}
            <Col md={4} xs={12} className='mb-3 mb-md-0'>
              <Form.Label className='fw-semibold'>
                Department <span className='text-danger'>*</span>
              </Form.Label>
              <Autocomplete
                disablePortal
                options={safeDeptOptions}
                getOptionLabel={(option: any) => option.name || ''}
                value={safeDeptOptions.find(dept => dept.id === formik.values.departmentId) || null}
                onChange={(_, value) => {
                  formik.setFieldValue('departmentId', value?.id || '')
                }}
                onBlur={() => {
                  formik.setFieldTouched('departmentId', true)
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={departments.length === 0 ? 'Select Organisation first' : 'Select Department'}
                    error={formik.touched.departmentId && Boolean(formik.errors.departmentId)}
                    helperText={formik.touched.departmentId && formik.errors.departmentId}
                    fullWidth
                    size='small'
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#dc3545',
                        },
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                )}
                disabled={departments.length === 0}
              />
            </Col>

            {/* Team Name */}
            <Col md={4} xs={12}>
              <Form.Label className='fw-semibold'>
                Team Name <span className='text-danger'>*</span>
              </Form.Label>
              <Form.Control
                name='team_name'
                placeholder='Enter Team Name'
                value={formik.values.team_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.team_name && !!formik.errors.team_name}
                style={{
                  borderColor: formik.touched.team_name && formik.errors.team_name ? '#dc3545' : '',
                  boxShadow: formik.touched.team_name && formik.errors.team_name ? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)' : ''
                }}
              />
              <Form.Control.Feedback 
                type='invalid' 
                style={{
                  color: '#dc3545',
                  fontSize: '0.875rem'
                }}
              >
                {formik.errors.team_name}
              </Form.Control.Feedback>
            </Col>
          </Row>

          <div className='d-flex justify-content-center gap-3 mt-3'>
            <Button variant="outline-secondary"
              style={{
                color: "#878899",
                borderColor: "#B0BAC9",
                backgroundColor: "transparent",
              }} onClick={() => navigate('/team-list')}>
              Cancel
            </Button>
            <Button className="btn"
              style={{
                backgroundColor: '#0073BB',
                borderColor: '#0073BB',
                color: '#fff'
              }} type='submit'>
              {id ? 'Update' : 'Submit'}
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default TeamstForm